import React, { useState, useRef, useEffect } from 'react';
import { Query } from 'react-apollo';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { OBbusinessPartnerVendorQuery } from '../../../../queries/Queries';
import { Loading } from '../../../Loading/Loading';

const VendorSearch = ({ value, onChange, placeholder, disabled, onServiceReferenceUpdate, onVendorSelect }) => {
  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [hasBeenCleared, setHasBeenCleared] = useState(false);
  const typeaheadRef = useRef(null);
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Build the where clause for vendor search with Vendor category
  const whereClause = (() => {
    const baseCondition = `businessPartnerCategory.name = 'Vendor'`;

    if (query && query.length > 0) {
      // If user is actively searching, filter by search query
      return `${baseCondition} and lower(name) like '%${query.toLowerCase()}%'`;
    } else if (value && !hasBeenCleared) {
      // If we have a selected value but no search query, and it hasn't been cleared, ensure it's included
      // Use id instead of mdmIdentificationNumber for reliable matching
      return `${baseCondition} and id = '${value}'`;
    } else {
      // Default: just show vendors (limited results)
      return baseCondition;
    }
  })();

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
    // Reset cleared flag when user starts typing again
    if (searchQuery && hasBeenCleared) {
      setHasBeenCleared(false);
    }
  };

  const handleChange = (selected) => {
    if (selected && selected.length > 0 && selected[0]) {
      setIsSelecting(true);
      setSelectedOption(selected[0]);
      setHasBeenCleared(false); // Reset cleared flag when making a selection

      // Get service reference if vendor type is Operator
      const operatorVendor = selected[0].mDMVendorTypeList.find((vendor) => vendor.vendorType === 'Operator');
      const serviceReference =
        operatorVendor && operatorVendor.serviceReference ? operatorVendor.serviceReference : null;

      // Update form context with service reference
      if (onServiceReferenceUpdate) {
        onServiceReferenceUpdate(serviceReference);
      }

      // Update parent with the full vendor object for payload transformation
      if (onVendorSelect) {
        onVendorSelect(selected[0]);
      }

      if (onChange) {
        // Send the id to the form for reliable tracking, not mdmIdentificationNumber
        onChange(selected[0].id);
      }

      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setIsSelecting(false), 100);
    } else {
      // Only clear if the form value is also null/undefined
      // This prevents clearing when AsyncTypeahead clears due to option not being in current data
      if (!value) {
        setSelectedOption(null);
        setHasBeenCleared(true); // Mark as explicitly cleared
        if (onServiceReferenceUpdate) {
          onServiceReferenceUpdate(null);
        }
        if (onVendorSelect) {
          onVendorSelect(null);
        }
        if (onChange) {
          onChange(null);
        }
      }
    }
  };

  const getSelectedValue = (options) => {
    // If user has explicitly cleared the selection, don't show any pre-selected option
    if (hasBeenCleared && !value) {
      return [];
    }

    // If we have a selectedOption, always use it (local state is authoritative)
    if (selectedOption) {
      return [selectedOption];
    }

    // If we have a form value but no matching local selectedOption, try to find it in the data
    if (value && options && options.length > 0) {
      const foundOption = options.find((option) => option.id === value);
      if (foundOption) {
        // Initialize selectedOption if we found it
        setSelectedOption(foundOption);
        setHasBeenCleared(false); // Reset cleared flag if we're restoring from form value
        return [foundOption];
      }
    }

    return [];
  };

  return (
    <Query query={OBbusinessPartnerVendorQuery} variables={{ where: whereClause }}>
      {({ loading, error, data }) => {
        if (error) {
          return <div>Error loading vendors: {error.message}</div>;
        }

        const options = (data && data.OB_businessPartner) || [];

        // Format options for display
        const formattedOptions = options.map((bp) => ({
          ...bp,
          displayLabel: `${bp.name} - ${bp.mdmIdentificationNumber}`,
        }));

        const selectedValue = getSelectedValue(formattedOptions);

        return (
          <AsyncTypeahead
            ref={typeaheadRef}
            filterBy={() => true} // Let the server handle filtering
            id='vendor-search'
            isLoading={loading || isSelecting}
            options={formattedOptions}
            labelKey='displayLabel'
            onSearch={handleSearch}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            selected={selectedValue}
            minLength={0}
            clearButton
            renderMenuItemChildren={(option) => (
              <div>
                <strong>{option.name}</strong> - {option.mdmIdentificationNumber}
              </div>
            )}
          >
            {isSelecting && <Loading />}
          </AsyncTypeahead>
        );
      }}
    </Query>
  );
};

export default VendorSearch;
